<script lang="ts">
  import { app, persistSettings } from './store.svelte';
  import { t } from './i18n';
  import type { InfillPattern } from '../core/types';
  import Icon from './lib/Icon.svelte';

  function update<K extends keyof typeof app.settings>(key: K, value: (typeof app.settings)[K]) {
    app.settings = { ...app.settings, [key]: value };
    persistSettings();
  }

  const patterns: { value: InfillPattern; key: 'pattern.none' | 'pattern.lines' | 'pattern.grid' | 'pattern.concentric' | 'pattern.hilbert' }[] = [
    { value: 'none', key: 'pattern.none' },
    { value: 'lines', key: 'pattern.lines' },
    { value: 'grid', key: 'pattern.grid' },
    { value: 'concentric', key: 'pattern.concentric' },
    { value: 'hilbert', key: 'pattern.hilbert' }
  ];
</script>

<section class="panel">
  <h3><Icon name="sliders-horizontal" size={11} /> Dilimleme Ayarları</h3>

  <label class="vase">
    <input
      type="checkbox"
      checked={app.settings.vaseMode}
      onchange={(e) => update('vaseMode', (e.currentTarget as HTMLInputElement).checked)}
    />
    <span>{t(app.lang, 'set.vaseMode')}</span>
  </label>

  <label>
    <span>{t(app.lang, 'set.layerHeight')}</span>
    <input
      type="number"
      min="0.4"
      max="5"
      step="0.1"
      value={app.settings.layerHeight}
      oninput={(e) => update('layerHeight', parseFloat((e.currentTarget as HTMLInputElement).value) || 1)}
    />
  </label>

  <label>
    <span>{t(app.lang, 'set.firstLayerHeight')}</span>
    <input
      type="number"
      min="0.4"
      max="5"
      step="0.1"
      value={app.settings.firstLayerHeight}
      oninput={(e) => update('firstLayerHeight', parseFloat((e.currentTarget as HTMLInputElement).value) || 1)}
    />
  </label>

  {#if !app.settings.vaseMode}
    <label>
      <span>{t(app.lang, 'set.perimeters')}</span>
      <input
        type="number"
        min="1"
        max="6"
        step="1"
        value={app.settings.perimeters}
        oninput={(e) => update('perimeters', parseInt((e.currentTarget as HTMLInputElement).value) || 1)}
      />
    </label>

    <label>
      <span>{t(app.lang, 'set.bottomLayers')}</span>
      <input
        type="number"
        min="0"
        max="10"
        step="1"
        value={app.settings.bottomLayers}
        oninput={(e) => update('bottomLayers', parseInt((e.currentTarget as HTMLInputElement).value) || 0)}
      />
    </label>

    <label>
      <span>{t(app.lang, 'set.topLayers')}</span>
      <input
        type="number"
        min="0"
        max="10"
        step="1"
        value={app.settings.topLayers}
        oninput={(e) => update('topLayers', parseInt((e.currentTarget as HTMLInputElement).value) || 0)}
      />
    </label>

    <label>
      <span>{t(app.lang, 'set.infillPattern')}</span>
      <select
        value={app.settings.infillPattern}
        onchange={(e) => update('infillPattern', (e.currentTarget as HTMLSelectElement).value as InfillPattern)}
      >
        {#each patterns as p (p.value)}
          <option value={p.value}>{t(app.lang, p.key)}</option>
        {/each}
      </select>
    </label>

    <label>
      <span>{t(app.lang, 'set.infillDensity')}</span>
      <input
        type="number"
        min="0"
        max="100"
        step="5"
        value={Math.round(app.settings.infillDensity * 100)}
        oninput={(e) => update('infillDensity', (parseFloat((e.currentTarget as HTMLInputElement).value) || 0) / 100)}
      />
    </label>

    <label>
      <span>{t(app.lang, 'set.infillAngle')}</span>
      <input
        type="number"
        min="0"
        max="180"
        step="5"
        value={app.settings.infillAngle}
        oninput={(e) => update('infillAngle', parseFloat((e.currentTarget as HTMLInputElement).value) || 0)}
      />
    </label>

    <label>
      <span>{t(app.lang, 'set.rotateInfill')}</span>
      <input
        type="number"
        min="0"
        max="90"
        step="5"
        value={app.settings.rotateInfillEachLayer}
        oninput={(e) => update('rotateInfillEachLayer', parseFloat((e.currentTarget as HTMLInputElement).value) || 0)}
      />
    </label>

    <label>
      <span>{t(app.lang, 'set.scaleInfill')}</span>
      <input
        type="number"
        min="0.2"
        max="5"
        step="0.1"
        value={app.settings.scaleInfill}
        oninput={(e) => update('scaleInfill', parseFloat((e.currentTarget as HTMLInputElement).value) || 1)}
      />
    </label>

    <label>
      <span>{t(app.lang, 'set.infillMargin')}</span>
      <input
        type="number"
        min="0"
        max="20"
        step="0.5"
        value={app.settings.infillMargin}
        oninput={(e) => update('infillMargin', parseFloat((e.currentTarget as HTMLInputElement).value) || 0)}
      />
    </label>
  {/if}

  <label>
    <span>{t(app.lang, 'set.printSpeed')}</span>
    <input
      type="number"
      min="100"
      max="6000"
      step="100"
      value={app.settings.printSpeed}
      oninput={(e) => update('printSpeed', parseFloat((e.currentTarget as HTMLInputElement).value) || 1500)}
    />
  </label>

  <label>
    <span>{t(app.lang, 'set.firstLayerSpeed')}</span>
    <input
      type="number"
      min="100"
      max="3000"
      step="100"
      value={app.settings.firstLayerSpeed}
      oninput={(e) => update('firstLayerSpeed', parseFloat((e.currentTarget as HTMLInputElement).value) || 800)}
    />
  </label>

  <label>
    <span>{t(app.lang, 'set.travelSpeed')}</span>
    <input
      type="number"
      min="500"
      max="10000"
      step="100"
      value={app.settings.travelSpeed}
      oninput={(e) => update('travelSpeed', parseFloat((e.currentTarget as HTMLInputElement).value) || 3000)}
    />
  </label>

  <label class="vase">
    <input type="checkbox"
      checked={app.telemetryOptIn}
      onchange={(e) => { app.telemetryOptIn = (e.currentTarget as HTMLInputElement).checked; persistSettings(); }} />
    <span>{t(app.lang, 'set.telemetry')}</span>
  </label>
</section>

<style>
  .panel {
    display: flex; flex-direction: column;
  }
  h3 {
    margin: 0 0 8px; font-size: 10px; color: var(--text-3);
    text-transform: uppercase; letter-spacing: 0.1em; font-weight: 600;
    display: inline-flex; align-items: center; gap: 6px;
  }
  label {
    display: flex; align-items: center; gap: 8px;
    font-size: 12px; padding: 6px 0;
    border-bottom: 1px dashed var(--border-dashed);
  }
  label:last-of-type { border-bottom: none; }
  label span { flex: 1; color: var(--text-2); }
  input[type='number'] {
    width: 80px; padding: 4px 8px;
    background: var(--chip-bg); color: var(--text-bright);
    border: 1px solid var(--border-soft); border-radius: 6px;
    font-size: 12px; font-family: inherit; text-align: right;
  }
  input[type='number']:focus { outline: none; border-color: var(--accent); }
  select {
    padding: 4px 8px; background: var(--chip-bg); color: var(--text-bright);
    border: 1px solid var(--border-soft); border-radius: 6px;
    font-size: 12px; font-family: inherit;
  }
  select:focus { outline: none; border-color: var(--accent); }
  .vase {
    padding: 8px 10px;
    background: var(--accent-soft);
    border: 1px solid rgba(26, 122, 201, 0.35);
    border-radius: 8px;
    color: #bfe6ff; font-weight: 500;
    margin-bottom: 8px;
  }
  .vase input { width: auto; }
</style>
